"use client";

import clsx from "clsx";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Bar} from "@/components/common/charts/Bar";
import {useUserProfile} from "@/hooks/useUserProfile";
import {CallByDayStat, ConversationByDayStat} from "@/types/admin/dashboard";
import {BarDataKeyedType} from "@/utils/common/charts";
import {GetTheme} from "@/utils/common/general";
import {GetDate_ISO} from "@/utils/common/generic";
import {GetPrimaryColorForTheme} from "@/utils/library/color";

function BarChartTooltip({
   indexValue,
   value,
   tooltipKey,
}: Readonly<{indexValue: string | number; value: number; tooltipKey: string}>) {
   const {t} = useTranslation("stats");

   return (
      <div className={clsx("p-2", "bg-panel", "shadow-lg", "rounded-lg", "border")}>
         {t(tooltipKey, {
            date: indexValue,
            count: value,
         })}
      </div>
   );
}

type ChannelId = "phoneCall" | "sms" | "liveChat";

const CHANNEL_TOOLTIP_KEYS: Record<ChannelId, string> = {
   phoneCall: "communicationMetrics.tooltipCalls",
   sms: "communicationMetrics.tooltipSms",
   liveChat: "communicationMetrics.tooltipWebchats",
};

const CHANNEL_TITLE_KEYS: Record<ChannelId, string> = {
   phoneCall: "communicationMetrics.callsOverTime",
   sms: "communicationMetrics.smsOverTime",
   liveChat: "communicationMetrics.webchatsOverTime",
};

interface CommunicationMetricsChartProps {
   callsByDay?: CallByDayStat[] | null | undefined;
   conversationsByDay?: ConversationByDayStat[] | null | undefined;
   channelId: ChannelId;
   days?: number;
}

export default function CommunicationMetricsChart({
   callsByDay,
   conversationsByDay,
   channelId,
   days = 7,
}: Readonly<CommunicationMetricsChartProps>) {
   const {t} = useTranslation("stats");
   const {userProfile} = useUserProfile();

   const [data, setData] = useState<BarDataKeyedType<"date", "count">[]>([]);

   // Get current theme - consistent with how Bar/Pie chart components handle theming
   const theme = GetTheme();

   const tooltipKey = CHANNEL_TOOLTIP_KEYS[channelId];

   // Memoize tooltip renderer to avoid recreating on each render
   const renderTooltip = useCallback(
      (d: {indexValue: string | number; value: number}) => (
         <BarChartTooltip indexValue={d.indexValue} value={d.value} tooltipKey={tooltipKey} />
      ),
      [tooltipKey]
   );

   useEffect(() => {
      const barColor = GetPrimaryColorForTheme(theme);
      const barData: BarDataKeyedType<"date", "count">[] = [];

      if (channelId === "phoneCall") {
         // Phone call data: aggregate inbound + outbound per day
         if (!callsByDay || callsByDay.length === 0) {
            setData([]);
            return;
         }

         for (let i = 0; i < days; i++) {
            const date = new Date();
            date.setDate(date.getDate() - (days - 1) + i);

            const dateString = GetDate_ISO(date);
            const dateLabel = date.toLocaleDateString(userProfile.language, {
               month: "short",
               day: "numeric",
            });

            const inboundStat = callsByDay.find(
               (stat) => stat.direction === "inbound" && stat.created_at.includes(dateString)
            );
            const outboundStat = callsByDay.find(
               (stat) => stat.direction === "outbound" && stat.created_at.includes(dateString)
            );

            const totalCount = (inboundStat?.count || 0) + (outboundStat?.count || 0);

            barData.push({
               date: dateLabel,
               count: totalCount,
               countColor: barColor,
            });
         }
      } else {
         // SMS or Webchat data: simple created_at + count
         if (!conversationsByDay || conversationsByDay.length === 0) {
            setData([]);
            return;
         }

         for (let i = 0; i < days; i++) {
            const date = new Date();
            date.setDate(date.getDate() - (days - 1) + i);

            const dateString = GetDate_ISO(date);
            const dateLabel = date.toLocaleDateString(userProfile.language, {
               month: "short",
               day: "numeric",
            });

            const dayStat = conversationsByDay.find((stat) =>
               stat.created_at.includes(dateString)
            );

            barData.push({
               date: dateLabel,
               count: dayStat?.count || 0,
               countColor: barColor,
            });
         }
      }

      setData(barData);
   }, [callsByDay, conversationsByDay, channelId, days, userProfile.language, theme]);

   const titleKey = CHANNEL_TITLE_KEYS[channelId];

   return (
      <div className={clsx("flex", "flex-col", "h-full")}>
         <h3 className={clsx("text-lg", "font-medium", "text-900", "mb-4")}>{t(titleKey)}</h3>
         <div className={clsx("flex-1", "min-h-[200px]")}>
            {data.length > 0 ? (
               <Bar
                  data={data}
                  keys={["count"]}
                  indexBy="date"
                  groupMode="grouped"
                  padding={0.65}
                  enableLabel={false}
                  tooltip={renderTooltip}
                  themeOverride={theme}
               />
            ) : (
               <div
                  className={clsx("h-full", "flex", "items-center", "justify-center", "text-500")}
               >
                  {t("noStats")}
               </div>
            )}
         </div>
      </div>
   );
}
